import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { errorsService } from "../../services/admin/errors.service";
import type {
  CreateErrorInput,
  UpdateErrorInput,
} from "../../services/admin/errors.service";

export function useErrorsAdmin() {
  return useQuery({
    queryKey: ["admin", "errors"],
    queryFn: errorsService.getAll,
  });
}

export function useErrorAdmin(id: string) {
  return useQuery({
    queryKey: ["admin", "errors", id],
    queryFn: () => errorsService.getById(id),
    enabled: !!id,
  });
}

export function useCreateError() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateErrorInput) => errorsService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "errors"] });
      notifications.show({
        title: "Success",
        message: "Error created successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to create error",
        color: "red",
      });
    },
  });
}

export function useUpdateError() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateErrorInput) => errorsService.update(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "errors"] });
      notifications.show({
        title: "Success",
        message: "Error updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update error",
        color: "red",
      });
    },
  });
}

export function useDeleteError() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => errorsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "errors"] });
      notifications.show({
        title: "Success",
        message: "Error deleted successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to delete error",
        color: "red",
      });
    },
  });
}

export function useToggleErrorActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      errorsService.toggleActive(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "errors"] });
      notifications.show({
        title: "Success",
        message: "Status updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update status",
        color: "red",
      });
    },
  });
}
